// «Stengte dager og endringer» under Tjenester og priser: stenge en dag eller uke, eller gi
// verkstedet flere eller færre minutter enn vanlig (KapasitetsOverstyring). Minuttene legges til
// budsjettet (minus trekker fra), som i components/workshop/capacityCheckLogic.js.
import React, { useMemo, useState } from 'react';
import { Valgfelt, Valgknapper, Tekstfelt, feltKlasse, knappPrimar, knappSekundar } from '../../ny-kundesamhandling/Skjema';
import Ikon from '../../ny-kundesamhandling/Ikon';
import { LOKASJON_NAVN, lagreIKatalog } from './katalogFelles';
import { Avkrysning, Tallfelt, Melding } from './Velgere';

const GJELDER = [
  { verdi: 'dato', tittel: 'En dag' },
  { verdi: 'uke', tittel: 'En uke' },
  { verdi: 'tidliguke', tittel: 'Tidlig i uken' },
  { verdi: 'senuke', tittel: 'Sent i uken' },
];
const GJELDER_NAVN = Object.fromEntries(GJELDER.map((g) => [g.verdi, g.tittel]));

const iDag = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Oslo' });
const hvilket = (o) => GJELDER.find((g) => o[g.verdi])?.verdi;
const datoFor = (o) => o[hvilket(o)] || '';

function visDato(o) {
  const felt = hvilket(o);
  const d = new Date(`${o[felt]}T12:00:00`);
  if (felt === 'dato') return d.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const man = new Date(d);
  man.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${GJELDER_NAVN[felt]}, uken som starter ${man.toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function NyOverstyring({ lokasjoner, onLagret, onAvbryt }) {
  const [skjema, setSkjema] = useState({
    lokasjon: lokasjoner[0] || '', gjelder: 'dato', dato: '', stengt: true, minutter: null,
    paavirkerSykkel: true, paavirkerSki: true, kommentar: '',
  });
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const sett = (felt) => (v) => setSkjema((s) => ({ ...s, [felt]: v }));

  async function lagre(e) {
    e.preventDefault();
    if (!skjema.dato) { setStatus({ type: 'feil', tekst: 'Velg dato.' }); return; }
    if (!skjema.stengt && (skjema.minutter === null || skjema.minutter === '-')) { setStatus({ type: 'feil', tekst: 'Skriv minutter, eller kryss av for stengt.' }); return; }
    setStatus({ type: 'lagrer', tekst: '' });
    try {
      await lagreIKatalog('POST', 'overstyringer', {
        lokasjon: skjema.lokasjon,
        [skjema.gjelder]: skjema.dato,
        stengt: skjema.stengt,
        minutter: skjema.stengt ? null : skjema.minutter,
        paavirkerSykkel: skjema.paavirkerSykkel,
        paavirkerSki: skjema.paavirkerSki,
        kommentar: skjema.kommentar,
      });
      onLagret();
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  return (
    <form onSubmit={lagre} aria-labelledby="ov-ny-tittel" className="space-y-3 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-3 sm:p-3">
      <h2 id="ov-ny-tittel" className="text-base font-bold text-slate-900">Ny endring</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <Valgfelt id="ov-lok" etikett="Verksted" value={skjema.lokasjon} onChange={(e) => sett('lokasjon')(e.target.value)}>
          {lokasjoner.map((l) => <option key={l} value={l}>{LOKASJON_NAVN[l] || l}</option>)}
        </Valgfelt>
        <div className="min-w-0">
          <label htmlFor="ov-dato" className="mb-1 block font-semibold text-slate-800">
            {skjema.gjelder === 'dato' ? 'Dato' : 'En dag i uken'}
          </label>
          <input id="ov-dato" type="date" value={skjema.dato} onChange={(e) => sett('dato')(e.target.value)} className={feltKlasse} required />
        </div>
      </div>
      <Valgknapper id="ov-gjelder" etikett="Gjelder" valg={GJELDER} verdi={skjema.gjelder} onVelg={sett('gjelder')} />
      <Avkrysning id="ov-stengt" etikett="Stengt" hjelp="Ingen booking denne dagen eller uken." verdi={skjema.stengt} onEndre={sett('stengt')} />
      {!skjema.stengt && (
        <Tallfelt id="ov-min" etikett="Minutter" tillatMinus enhet="min"
          hjelp="Legges til vanlig kapasitet. Bruk minus for færre, f.eks. -120 når en mekaniker er borte en halv dag."
          verdi={skjema.minutter} onEndre={sett('minutter')} />
      )}
      <div className="flex flex-wrap gap-3">
        <Avkrysning id="ov-sykkel" etikett="Gjelder sykkel" verdi={skjema.paavirkerSykkel} onEndre={sett('paavirkerSykkel')} />
        <Avkrysning id="ov-ski" etikett="Gjelder ski og skøyter" verdi={skjema.paavirkerSki} onEndre={sett('paavirkerSki')} />
      </div>
      <Tekstfelt id="ov-kommentar" etikett="Kommentar (valgfritt)" hjelp="Bare for ansatte, f.eks. «Julaften»." value={skjema.kommentar} onChange={(e) => sett('kommentar')(e.target.value)} maxLength={300} />
      {status.type === 'feil' && <Melding type="feil">{status.tekst}</Melding>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={status.type === 'lagrer'} className={knappPrimar}>
          <Ikon navn="hake" className="w-6 h-6" /> {status.type === 'lagrer' ? 'Lagrer …' : 'Lagre'}
        </button>
        <button type="button" onClick={onAvbryt} className={knappSekundar}>Avbryt</button>
      </div>
    </form>
  );
}

function Rad({ o, kanEndre, onSlettet }) {
  const [feil, setFeil] = useState('');
  async function slett() {
    if (!window.confirm(`Slette «${visDato(o)}» for ${LOKASJON_NAVN[o.lokasjon] || o.lokasjon}?`)) return;
    try {
      await lagreIKatalog('DELETE', `overstyringer/${o._id}`);
      onSlettet();
    } catch (err) {
      setFeil(err.message);
    }
  }
  const hva = o.stengt ? 'Stengt' : `${o.minutter > 0 ? '+' : ''}${o.minutter} min`;
  const gjelder = [o.paavirkerSykkel && 'sykkel', o.paavirkerSki && 'ski'].filter(Boolean).join(' og ');
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-slate-200 bg-white p-2.5">
      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-sm font-bold ${o.stengt ? 'bg-red-100 text-red-900' : o.minutter < 0 ? 'bg-amber-100 text-amber-950' : 'bg-emerald-100 text-emerald-900'}`}>{hva}</span>
      <span className="min-w-0 flex-1 basis-56">
        <span className="block font-semibold text-slate-900 first-letter:uppercase">{visDato(o)}</span>
        <span className="block text-sm text-slate-700">
          {LOKASJON_NAVN[o.lokasjon] || o.lokasjon}{gjelder && ` · ${gjelder}`}{o.kommentar && ` · ${o.kommentar}`}
        </span>
      </span>
      {kanEndre && (
        <button type="button" onClick={slett} className={`${knappSekundar} min-h-[2.25rem] py-1.5`}>
          Slett<span className="sr-only"> {visDato(o)}</span>
        </button>
      )}
      {feil && <p role="alert" className="w-full text-sm font-semibold text-red-800">{feil}</p>}
    </li>
  );
}

export default function Overstyringer({ data, kanEndre, hent }) {
  const [visNy, setVisNy] = useState(false);
  const lokasjoner = useMemo(() => [...new Set(data.kapasiteter.map((k) => k.lokasjonsNavn).filter(Boolean))], [data.kapasiteter]);
  // Uker og dager som er over: en uke regnes som over sju dager etter datoen som står på den.
  const grense = useMemo(() => {
    const d = new Date(`${iDag()}T12:00:00`);
    d.setDate(d.getDate() - 7);
    return d.toLocaleDateString('sv-SE');
  }, []);
  const sortert = [...data.overstyringer].sort((a, b) => datoFor(a).localeCompare(datoFor(b)));
  const kommende = sortert.filter((o) => (o.dato ? o.dato >= iDag() : datoFor(o) >= grense));
  const tidligere = sortert.filter((o) => !kommende.includes(o)).reverse();

  return (
    <div className="space-y-3">
      <p className="text-slate-700">Stenge en dag eller uke, eller gi verkstedet flere eller færre minutter enn vanlig. Bookingen på nettsiden følger dette med en gang.</p>
      {kanEndre && !visNy && (
        <button type="button" onClick={() => setVisNy(true)} className={knappSekundar}><Ikon navn="pluss" className="w-5 h-5" /> Ny endring</button>
      )}
      {visNy && <NyOverstyring lokasjoner={lokasjoner} onLagret={() => { setVisNy(false); hent(); }} onAvbryt={() => setVisNy(false)} />}
      <section aria-labelledby="ov-kommende">
        <h2 id="ov-kommende" className="mb-2 text-base font-bold text-slate-900">Kommende</h2>
        {kommende.length ? (
          <ul className="space-y-2">{kommende.map((o) => <Rad key={o._id} o={o} kanEndre={kanEndre} onSlettet={hent} />)}</ul>
        ) : (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-slate-700">Ingen stengte dager eller endringer fremover.</p>
        )}
      </section>
      {tidligere.length > 0 && (
        <details className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
          <summary className="cursor-pointer font-semibold text-slate-800">Tidligere ({tidligere.length})</summary>
          <ul className="mt-3 space-y-2">{tidligere.map((o) => <Rad key={o._id} o={o} kanEndre={false} onSlettet={hent} />)}</ul>
        </details>
      )}
    </div>
  );
}
